// components/Turnstile.tsx
import React, { useEffect, useRef, useState } from "react";
import { turnstileService } from "../services/turnstileService";
import { useTurnstileEnabled } from "../hooks/useFeatureFlag";

interface TurnstileProps {
  sitekey: string;
  onVerify: (token: string) => void;
  onError?: (error: string) => void;
  onExpire?: () => void;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
  className?: string;
}

export const Turnstile: React.FC<TurnstileProps> = ({
  sitekey,
  onVerify,
  onError,
  onExpire,
  theme = "auto",
  size = "normal",
  className = "",
}) => {
  const isEnabled = useTurnstileEnabled();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEnabled) {
      setIsAvailable(false);
      return;
    }

    let mounted = true;

    const initTurnstile = async () => {
      try {
        const available = await turnstileService.checkAvailability();

        if (!mounted) return;

        if (available) {
          setIsAvailable(true);
        } else {
          setIsAvailable(false);
          setError("CAPTCHA service is currently unavailable");
        }
      } catch (err) {
        if (!mounted) return;
        setIsAvailable(false);
        setError("Failed to load CAPTCHA service");
      }
    };

    initTurnstile();

    return () => {
      mounted = false;
      if (widgetIdRef.current) {
        turnstileService.removeWidget(widgetIdRef.current);
      }
    };
  }, [isEnabled]);

  useEffect(() => {
    if (!isEnabled || !isAvailable || !containerRef.current) {
      return;
    }

    if (widgetIdRef.current) {
      turnstileService.removeWidget(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    containerRef.current.innerHTML = "";

    const widgetId = turnstileService.renderWidget(containerRef.current, {
      sitekey,
      callback: (token: string) => {
        console.log("Turnstile verified");
        onVerify(token);
      },
      onError: (errorMsg: string) => {
        console.error("Turnstile error:", errorMsg);
        setError(errorMsg);
        if (onError) onError(errorMsg);
      },
      onExpire: () => {
        console.log("Turnstile expired");
        if (onExpire) onExpire();
      },
      theme,
      size,
    });

    if (widgetId) {
      widgetIdRef.current = widgetId;
    } else {
      setError("Failed to render CAPTCHA widget");
    }

    return () => {
      if (widgetIdRef.current) {
        turnstileService.removeWidget(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [
    isEnabled,
    isAvailable,
    sitekey,
    theme,
    size,
    onVerify,
    onError,
    onExpire,
  ]);

  if (!isEnabled) {
    return (
      <div
        className={`p-3 bg-gray-50 border border-gray-200 rounded-md ${className}`}
      >
        <p className="text-gray-500 text-sm">
          Verification is currently disabled
        </p>
      </div>
    );
  }

  if (isAvailable === null) {
    return (
      <div
        className={`flex items-center gap-2 text-gray-500 text-sm p-3 bg-gray-50 rounded-md ${className}`}
      >
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-primary"></div>
        <span>Loading verification...</span>
      </div>
    );
  }

  if (error || isAvailable === false) {
    return (
      <div
        className={`p-3 bg-red-50 border border-red-200 rounded-md ${className}`}
      >
        <p className="text-red-600 text-sm">
          {error || "CAPTCHA service unavailable"}
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-primary hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`turnstile-widget ${className}`}
      style={{ minHeight: "65px" }}
    />
  );
};
